import { Theme, createTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

enum COLORS {
  LIGHT_SKY_BLUE = "#EBF3FF",
  LIGHT_BLACK = "#1C274C",
  WHITE = "#FFFFFF",
  LIGHT_GREY = "#E2E8F0",
  GRAY = "#6C727F",
  GRAY_BLUE = "#F1F5F9",
  TEXT_BLACK = "#1E293B",
  SHADOW_GRAY = "#0000001A",
  LIGHT_GREEN = "#DCFCE7",
  GREEN = "#22C55E",
  LIGHT_BLUE = "#E0E7FF",
  DARK_BLUE = "#6366F1",
  LIGHT_YELLOW = "#FEF3C7",
  DARK_YELLOW = "#F59E0B",
  LIGHT_PURPLE = "#EDE9FE",
  DARK_PURPLE = "#8B5CF6",
  LIGHTER_BLUE = "#F3F7FC",
  LIGHT_BLUE_PREVIEW_HEADER = "#599AF2",
  // PRIMARY_GREEN = "#6CCA98",
  // BLACK = "#000000",
  // PRIMARY_MAGENTA = "#863399",
  // LIGHT_GREEN = "#62cbc9",
  // GRAY = "#eceff4",
  // DARK_GRAY = "#616a78",
  // DARK_GRAY_SCROLL = "#444444",
  // LIGHT_COLOR_SCROLL = "#ecf2fa",
  // TEAL_GREEN = "#41795b",
  // SEA_GREEN = "#56a17a",
  // CHARCOAL_GRAY = "#21272a",
  // MATTERHORN = "#4e4e4e",
  // OXFORD_BLUE = "#0f1b4d",
  // BLIZZARD_BLUE = "#a1b1c8",
  // RED = "#f00",
  // MISTY_BLUE = "#a1b2c8",
}

export type MyTheme = Theme & {
  palette: {
    otherFeatures: {
      titleColor: string;
      bgColor: string;
      textColor: string;
      border: string;
      shadowColor: string;
      previewHeader: string;
    };
    auth: {
      primary: string;
    };
    text: {
      gray: string;
    };
    event: {
      [key in "click" | "install" | "cr" | "sent"]: {
        bgColor: string;
        textColor: string;
        graphColor: string;
      };
    };
  };
};

export interface IWithThemeProps {
  theme?: MyTheme;
}

export interface IWithClassName {
  className?: string;
}

export const event = {
  click: {
    bgColor: COLORS.LIGHT_PURPLE,
    textColor: COLORS.DARK_PURPLE,
    graphColor: "#D0BDFb",
  },
  install: {
    bgColor: COLORS.LIGHT_YELLOW,
    textColor: COLORS.DARK_YELLOW,
    graphColor: COLORS.LIGHT_YELLOW,
  },
  cr: {
    bgColor: COLORS.LIGHT_GREEN,
    textColor: COLORS.GREEN,
    graphColor: "#A1E3DC",
  },
  sent: {
    bgColor: COLORS.LIGHT_BLUE,
    textColor: COLORS.DARK_BLUE,
    graphColor: "#8FC5F5",
  },
};

export const defaultTheme = createTheme(
  {
    breakpoints: {
      values: {
        xs: 300,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    palette: {
      background: {
        default: COLORS.WHITE,
      },
      text: {
        primary: COLORS.LIGHT_BLACK,
      },
    },
    typography: {
      fontFamily: "var(--font-family)",
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            backgroundColor: COLORS.LIGHT_SKY_BLUE,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            width: "fit-content",
            textTransform: "none",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: "small",
          variant: "filled",
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: "small",
          variant: "filled",
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontWeight: 600,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: ({ theme }) => ({
            color: theme.palette.error.main,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 5,
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: COLORS.LIGHTER_BLUE,
          }),
          label: ({ theme }) => ({
            color: theme.palette.primary.main,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            borderRight: `1px solid ${theme.palette.primary.main}`,
          }),
          deleteIcon: ({ theme }) => ({
            color: theme.palette.primary.main,
            paddingLeft: 8,
          }),
        },
        defaultProps: {
          deleteIcon: <CloseIcon />,
        },
      },
      MuiTable: {
        defaultProps: {
          size: "small",
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.spacing(0.6),
            margin: 0,
            svg: {
              width: 25,
            },
            "&:hover": {
              backgroundColor: theme.palette.grey[100],
            },
            "&:has(span.Mui-checked)": {
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              svg: {
                path: {
                  fill: theme.palette.primary.main,
                },
              },
              backgroundColor: COLORS.LIGHTER_BLUE,
            },
          }),
          label: {
            fontWeight: 600,
          },
        },
      },
    },
  },
  {
    palette: {
      background: {
        default: COLORS.GRAY_BLUE,
      },
      otherFeatures: {
        titleColor: COLORS.LIGHT_BLACK,
        textColor: COLORS.WHITE,
        shadowColor: COLORS.SHADOW_GRAY,
        border: COLORS.LIGHT_GREY,
        previewHeader: COLORS.LIGHT_BLUE_PREVIEW_HEADER,
      },
      auth: {
        primary: COLORS.LIGHT_SKY_BLUE,
      },
      text: {
        gray: COLORS.GRAY,
        primary: COLORS.TEXT_BLACK,
      },
      event,
    },
  } as MyTheme,
);
